// AppShell — sidebar + topbar layout.
// Converted from the Claude prototype (shell.jsx) to typed TypeScript.
// Navigation is driven by React Router — no prop drilling for active/onNav.
import { useState, useEffect, useMemo, useRef } from 'react';
import { OperationCreateModal } from '../components/OperationCreateModal';
import { CloseOperationModal } from '../components/CloseOperationModal';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { UserRole, Operation } from '@vigilo/shared';
import type { NavGroup as NavGroupConfig } from '../shell/AppContext';
import { useApp } from '../shell/AppContext';
import { useMockMode } from '../shell/MockModeContext';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
} from '../app/hooks/useCrowdActivityAudit';
import { useVenues } from '../app/hooks/useVenues';
import {
  type ColorPalette, Icons, KLabel, StatusDot, StatusColor,
  FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, LINE_HEIGHT,
  SPACE, RADIUS, SIZE,
} from '@vigilo/design-system';
import { operationStatusColor } from './crowdPalette';

// ─── Screen metadata ──────────────────────────────────────────────────────────

const SCREEN_META: Record<string, { title: string; sub: string }> = {
  operations: { title: 'Operations',          sub: 'Select or manage operation context' },
  map:      { title: 'Map Dashboard',       sub: 'Live crowd density, alerts, and zone status' },
  venues:   { title: 'Venues',              sub: 'Manage venues and their zone configurations' },
  'admin/zones': { title: 'Zones',          sub: 'Configure zones across venues' },
  zones:    { title: 'Zone Management',     sub: 'Monitored zones — list, create, configure' },
  alerts:   { title: 'Alert Management',    sub: 'Active alerts and lifecycle workflow' },
  command:  { title: 'Command Dashboard',   sub: 'Multi-zone situational awareness — Supervisor view' },
  movement: { title: 'Movement & Playback', sub: 'Entry/exit flow analytics and historical replay' },
  flags:    { title: 'Flagged Tokens',      sub: 'Session-bound anonymous token flagging' },
  reports:  { title: 'Reporting & Export',  sub: 'Generate and export operational reports' },
  admin:    { title: 'Administration',      sub: 'Users, teams, venues, zones, presets, feeds, simulation, audit' },
};

const CROWD_ROLE_RANK: Partial<Record<UserRole, number>> = {
  crowd_analyst:      1,
  command_supervisor: 2,
  system_admin:       3,
};

function hasRequiredRole(role: UserRole, requiredRole?: UserRole): boolean {
  if (!requiredRole) return Boolean(CROWD_ROLE_RANK[role]);
  const roleRank = CROWD_ROLE_RANK[role];
  const requiredRank = CROWD_ROLE_RANK[requiredRole];
  return roleRank !== undefined && requiredRank !== undefined && roleRank >= requiredRank;
}

// ─── Operation elapsed timer ──────────────────────────────────────────────────

function useOpElapsed(startIso: string | null): string {
  const [elapsed, setElapsed] = useState('00:00:00');
  useEffect(() => {
    if (!startIso) { setElapsed('—'); return; }
    const tick = () => {
      const diff = Math.max(0, Math.floor((Date.now() - new Date(startIso).getTime()) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed([h, m, s].map(n => String(n).padStart(2, '0')).join(':'));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startIso]);
  return elapsed;
}

// ─── OperationCard ────────────────────────────────────────────────────────────

interface OperationCardProps {
  C:               ColorPalette;
  selected:        Operation | null;
  all:             Operation[];
  role:            UserRole;
  onSelect:        (op: Operation) => void;
  onNewOperation:  () => void;
  onCloseOperation: () => void;
  venueLabel:      (venueId: string) => string;
}

function OperationCard({ C, selected, all, role, onSelect, onNewOperation, onCloseOperation, venueLabel }: OperationCardProps) {
  const [open, setOpen]   = useState(false);
  const dropRef           = useRef<HTMLDivElement>(null);
  const elapsed           = useOpElapsed(selected?.actualStart ?? null);
  const canStart          = role === 'command_supervisor' || role === 'system_admin';
  const hasMultiple       = all.length > 1;
  const selectedIndex     = selected ? all.findIndex(op => op.id === selected.id) : -1;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // STATE 3 — no active operations
  if (!selected) {
    return (
      <div style={{
        background: C.faint, border: `1px solid ${C.border}`,
        borderRadius: RADIUS.card, padding: `${SPACE[10]}px`,
      }}>
        <div style={{
          fontSize: FONT_SIZE.body, color: C.muted, fontStyle: 'italic', marginBottom: SPACE[8],
        }}>No active operation</div>
        {canStart && (
          <button onClick={onNewOperation} style={{
            width: '100%', padding: `${SPACE[6]}px`, borderRadius: RADIUS.card,
            background: 'transparent', border: `1px solid ${C.amber}55`,
            color: C.amber, fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold,
            cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACE[4],
          }}>
            <Icons.Plus size={11} /> Start Operation
          </button>
        )}
      </div>
    );
  }

  const venueName = venueLabel(selected.venueId);
  // Shared StatusColor has no 'paused' key; handle locally (amber).
  const statusColor = selected.status === 'paused' ? C.amber : StatusColor(C, selected.status);
  const opDate    = new Date(selected.scheduledStart).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  return (
    <div ref={dropRef} style={{ position: 'relative' }}>
      {/* STATE 1 / 2 — card */}
      <div
        onClick={() => hasMultiple && setOpen(o => !o)}
        style={{
          background: C.faint, border: `1px solid ${C.border}`,
          borderRadius: RADIUS.card, padding: `${SPACE[8]}px ${SPACE[10]}px`,
          cursor: hasMultiple ? 'pointer' : 'default',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[4], marginBottom: SPACE[2] }}>
              <div style={{
                fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
                letterSpacing: LETTER_SPACING.klabel, textTransform: 'uppercase',
                color: C.amber,
              }}>Active Operation</div>
              {canStart && (
                <button
                  onClick={e => { e.stopPropagation(); onNewOperation(); }}
                  style={{
                    background: 'transparent', border: `1px solid ${C.amber}55`,
                    borderRadius: RADIUS.sm, padding: `1px ${SPACE[4]}px`,
                    color: C.amber, cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                    fontFamily: "'Albert Sans', sans-serif",
                  }}
                  title="Start new operation"
                >
                  <Icons.Plus size={9} />
                </button>
              )}
            </div>
            <div style={{
              fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold,
              color: C.text, lineHeight: LINE_HEIGHT.badge,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{selected.name}</div>
            {hasMultiple && (
              <div style={{ fontSize: FONT_SIZE.label, color: C.muted, marginTop: SPACE[1] }}>
                {Math.max(selectedIndex + 1, 1)} of {all.length} operations
              </div>
            )}
            <div style={{ fontSize: FONT_SIZE.micro, fontFamily: 'monospace', color: C.muted, marginTop: SPACE[1] }}>
              {venueName} · {opDate}
            </div>
            {canStart && (
              <button
                onClick={e => { e.stopPropagation(); onCloseOperation(); }}
                style={{
                  background: 'none', border: 'none', padding: 0, marginTop: SPACE[4],
                  fontSize: FONT_SIZE.xs, color: C.ember, cursor: 'pointer',
                  fontFamily: "'Albert Sans', sans-serif",
                  textAlign: 'left',
                }}
              >
                Close operation
              </button>
            )}
          </div>
          {hasMultiple && (
            <span style={{ color: C.muted, flexShrink: 0, marginTop: SPACE[2], marginLeft: SPACE[4] }}>
              <Icons.Down size={12} />
            </span>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACE[4], marginTop: SPACE[6],
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: statusColor,
            boxShadow: `0 0 0 2px ${statusColor}30`, flexShrink: 0,
          }} />
          <span style={{
            fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
            color: statusColor, letterSpacing: LETTER_SPACING.field, textTransform: 'uppercase',
          }}>{selected.status}</span>
          <span style={{ fontSize: FONT_SIZE.xs, fontFamily: 'monospace', color: C.muted, marginLeft: SPACE[2] }}>
            {elapsed}
          </span>
        </div>
      </div>

      {/* STATE 2 — dropdown */}
      {open && hasMultiple && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: SPACE[2],
          background: C.panel, border: `1px solid ${C.border2}`, borderRadius: RADIUS.card,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 200, overflow: 'hidden',
        }}>
          {all.map(op => {
            const isSelected = op.id === selected.id;
            return (
              <button
                key={op.id}
                onClick={() => { onSelect(op); setOpen(false); }}
                style={{
                  width: '100%', padding: `${SPACE[10]}px ${SPACE[12]}px`,
                  display: 'flex', alignItems: 'center', gap: SPACE[8],
                  background: isSelected ? C.amberDim : 'transparent',
                  borderLeft: isSelected ? `3px solid ${C.amber}` : '3px solid transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontFamily: "'Albert Sans', sans-serif",
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  background: operationStatusColor(C, op.status),
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold,
                    color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{op.name}</div>
                  <div style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
                    {venueLabel(op.venueId)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function userInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map(p => p[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface SidebarProps { C: ColorPalette; }

interface NavGroupProps {
  C: ColorPalette;
  group: NavGroupConfig;
  currentUserRole: UserRole;
  activeId: string;
  activePath: string;
  onNavigate: (path: string) => void;
  isFirst: boolean;
}

function NavGroup({ C, group, currentUserRole, activeId, activePath, onNavigate, isFirst }: NavGroupProps) {
  const visibleItems = group.items.filter(item => hasRequiredRole(currentUserRole, item.requiredRole));
  if (visibleItems.length === 0) return null;

  return (
    <div style={{ marginTop: isFirst ? 0 : SPACE[18] }}>
      {group.label && <KLabel C={C}>{group.label}</KLabel>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[2] }}>
        {visibleItems.map(item => {
          const Icon = Icons[item.icon] ?? Icons.Circle;
          const isActive = activeId === item.id || activePath === item.path;
          const count = item.badgeCount ?? 0;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: SPACE[10],
                padding: `${SPACE[8]}px ${SPACE[10]}px`,
                borderRadius: RADIUS.card, fontSize: FONT_SIZE.item, fontWeight: FONT_WEIGHT.medium,
                fontFamily: "'Albert Sans', sans-serif",
                color: isActive ? C.text : C.sub,
                background: isActive ? C.amberDim : 'transparent',
                border: isActive ? `1px solid ${C.amber}40` : '1px solid transparent',
                cursor: 'pointer', textAlign: 'left', transition: 'all 120ms',
                width: '100%',
              }}
            >
              <span style={{ color: isActive ? C.amber : C.sub }}>
                <Icon size={SIZE.iconNav} />
              </span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {count > 0 && (
                <span style={{
                  fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                  background: isActive ? C.amber : C.faint,
                  color: isActive ? '#fff' : C.muted,
                  padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.pill,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Sidebar({ C }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    navConfig,
    selectedOperation,
    activeOperations,
    setSelectedOperation,
    openOperationModal,
    openCloseModal,
    logout,
  } = useApp();
  const { isMock, toggle } = useMockMode();
  const { venues } = useVenues();
  const recordActivity = useCrowdActivityAudit();
  const venueLabelsById = useMemo(
    () => new Map(venues.map(venue => [venue.id, venue.name])),
    [venues],
  );
  const venueLabel = (venueId: string) => venueLabelsById.get(venueId) ?? 'Unknown venue';

  const activeId = location.pathname.replace('/', '') || 'map';
  const selectOperation = (op: Operation) => {
    setSelectedOperation(op);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.operationSelected,
      page: location.pathname,
      operationId: op.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.operation,
      resourceId: op.id,
      metadata: { sourcePage: location.pathname, targetPage: location.pathname },
    });
  };

  return (
    <nav style={{
      width: SIZE.sidebar, background: C.sidebar, display: 'flex', flexDirection: 'column',
      flexShrink: 0, height: '100%', borderRight: `1px solid ${C.border2}`,
    }}>
      {/* Logo */}
      <div style={{ padding: `${SPACE[16]}px ${SPACE[18]}px ${SPACE[14]}px`, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[10] }}>
          <div style={{
            width: SIZE.avatarMd, height: SIZE.avatarMd, borderRadius: RADIUS.lg,
            background: C.amberDim, border: `1px solid ${C.amber}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.amber,
          }}>
            <Icons.Shield size={SIZE.icon} />
          </div>
          <div>
            <div style={{
              fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.bold,
              color: C.text, letterSpacing: LETTER_SPACING.btn,
            }}>VIGILO</div>
            <div style={{
              fontSize: FONT_SIZE.sidebarMark, fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.brand, textTransform: 'uppercase',
              color: C.muted, marginTop: SPACE[1],
            }}>
              Crowd Monitoring
            </div>
          </div>
        </div>
      </div>

      {/* Nav groups */}
      <div style={{ padding: `${SPACE[12]}px ${SPACE[8]}px ${SPACE[8]}px`, flex: 1, overflowY: 'auto' }}>
        {navConfig.map((group, index) => (
          <NavGroup
            key={group.id}
            C={C}
            group={group}
            currentUserRole={currentUser.role}
            activeId={activeId}
            activePath={location.pathname}
            onNavigate={navigate}
            isFirst={index === 0}
          />
        ))}

        {/* Operation switcher */}
        <div style={{ marginTop: SPACE[18] }}>
          <KLabel C={C}>Session</KLabel>
          <OperationCard
            C={C}
            selected={selectedOperation}
            all={activeOperations}
            role={currentUser.role}
            onSelect={selectOperation}
            onNewOperation={openOperationModal}
            onCloseOperation={openCloseModal}
            venueLabel={venueLabel}
          />
        </div>
      </div>

      {/* Data source toggle — dev only */}
      {import.meta.env['VITE_APP_ENV'] === 'development' && (
        <div style={{
          padding: `${SPACE[8]}px ${SPACE[12]}px`,
          borderTop:    `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{
            fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.semibold,
            letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
            color: C.muted, marginBottom: SPACE[4],
          }}>
            Data Source
          </div>
          <div style={{
            display: 'flex', borderRadius: RADIUS.card,
            background: C.bg, border: `1px solid ${C.border}`,
            overflow: 'hidden',
          }}>
            <button
              onClick={() => isMock && toggle()}
              style={{
                flex: 1, height: SPACE[28],
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACE[4],
                fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.semibold,
                letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
                background: !isMock ? C.surface : 'transparent',
                color: !isMock ? C.green : C.muted,
                border: 'none', cursor: 'pointer',
                fontFamily: "'Albert Sans', sans-serif",
                transition: 'all 120ms',
              }}
            >
              <span>{!isMock ? '●' : '○'}</span> Live
            </button>
            <button
              onClick={() => !isMock && toggle()}
              style={{
                flex: 1, height: SPACE[28],
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACE[4],
                fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.semibold,
                letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
                background: isMock ? C.surface : 'transparent',
                color: isMock ? C.amber : C.muted,
                border: 'none', cursor: 'pointer',
                fontFamily: "'Albert Sans', sans-serif",
                transition: 'all 120ms',
              }}
            >
              <span>{isMock ? '◆' : '◇'}</span> Mock
            </button>
          </div>
        </div>
      )}

      {/* Sign out */}
      <div style={{ padding: `${SPACE[8]}px ${SPACE[8]}px`, borderTop: `1px solid ${C.border}` }}>
        <button
          type="button"
          onClick={() => void logout()}
          style={{
            display: 'flex', alignItems: 'center', gap: SPACE[10],
            padding: `${SPACE[8]}px ${SPACE[10]}px`, borderRadius: RADIUS.md,
            fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.medium,
            fontFamily: "'Albert Sans', sans-serif",
            color: C.sub, background: 'transparent',
            border: '1px solid transparent',
            cursor: 'pointer', textAlign: 'left', transition: 'all 120ms',
            width: '100%',
          }}
        >
          <span style={{ color: C.sub }}>
            <Icons.Power size={SIZE.iconNav} />
          </span>
          <span style={{ flex: 1 }}>Sign Out</span>
        </button>
      </div>

      {/* User card */}
      <div style={{ padding: SPACE[12], borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
          <div style={{
            width: SIZE.avatarMd, height: SIZE.avatarMd,
            background: C.amberDim, border: `1px solid ${C.amber}50`,
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: C.amber, flexShrink: 0,
          }}>
            {userInitials(currentUser.displayName)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold,
              color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {currentUser.displayName}
            </div>
            <div style={{
              fontSize: FONT_SIZE.label, color: C.muted,
              letterSpacing: LETTER_SPACING.badge, textTransform: 'uppercase',
            }}>
              {currentUser.role.replace(/_/g, ' ')}
            </div>
          </div>
          <StatusDot color={C.green} pulse />
        </div>
      </div>
    </nav>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────────────────

interface TopbarProps {
  C:       ColorPalette;
  title:   string;
  sub:     string;
  simTime: string;
}

function Topbar({ C, title, sub, simTime }: TopbarProps) {
  const { activeAlertCount } = useApp();

  return (
    <div style={{
      height: SIZE.topbar, background: C.surface, borderBottom: `1px solid ${C.border2}`,
      display: 'flex', alignItems: 'center', padding: `0 ${SPACE[20]}px`, gap: SPACE[12], flexShrink: 0,
    }}>
      <div>
        <div style={{
          fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold,
          color: C.text, letterSpacing: LETTER_SPACING.tight,
        }}>{title}</div>
        {sub && <div style={{ fontSize: FONT_SIZE.basePlus, lineHeight: '14.7px', color: C.sub, marginTop: SPACE[1] }}>{sub}</div>}
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACE[8], alignItems: 'center' }}>
        {/* Live clock */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACE[6],
          background: C.faint, border: `1px solid ${C.border}`,
          borderRadius: RADIUS.md, padding: `${SPACE[4]}px ${SPACE[10]}px`,
        }}>
          <StatusDot color={C.green} pulse size={5} />
          <span style={{
            fontSize: FONT_SIZE.xs, color: C.muted,
            fontWeight: FONT_WEIGHT.semibold, letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
          }}>Live</span>
          <span style={{
            fontSize: FONT_SIZE.base, fontFamily: 'monospace',
            color: C.text, fontWeight: FONT_WEIGHT.semibold,
          }}>{simTime}</span>
        </div>

        {/* Alert badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACE[5],
          background: activeAlertCount > 0 ? `${C.critical}1f` : C.faint,
          border: `1px solid ${activeAlertCount > 0 ? `${C.critical}66` : C.border2}`,
          borderRadius: RADIUS.md, padding: `${SPACE[4]}px ${SPACE[10]}px`,
        }}>
          <span style={{
            width: SPACE[6], height: SPACE[6], borderRadius: '50%',
            background: activeAlertCount > 0 ? C.critical : C.muted,
            boxShadow: activeAlertCount > 0 ? `0 0 0 2px ${C.critical}30` : 'none',
          }} />
          <span style={{
            fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.bold,
            letterSpacing: LETTER_SPACING.badge,
            color: activeAlertCount > 0 ? C.critical : C.muted,
          }}>
            {activeAlertCount} ACTIVE {activeAlertCount === 1 ? 'ALERT' : 'ALERTS'}
          </span>
        </div>

        {/* Search */}
        <button style={{
          width: SPACE[32], height: SPACE[32], border: `1px solid ${C.border2}`, borderRadius: RADIUS.md,
          background: 'transparent', color: C.sub, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icons.Search size={SIZE.iconMd} />
        </button>

        {/* Notifications */}
        <button style={{
          width: SPACE[32], height: SPACE[32], border: `1px solid ${C.border2}`, borderRadius: RADIUS.md,
          background: 'transparent', color: C.sub, cursor: 'pointer', position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icons.Bell size={SIZE.iconMd} />
          {activeAlertCount > 0 && (
            <span style={{
              position: 'absolute', top: SPACE[4], right: SPACE[4],
              width: SPACE[7], height: SPACE[7], background: C.critical, borderRadius: '50%',
              boxShadow: `0 0 0 1.5px ${C.surface}`,
            }} />
          )}
        </button>
      </div>
    </div>
  );
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

function tickClock(prev: string): string {
  const [h, m, s] = prev.split(':').map(Number);
  const ns = (s + 1) % 60;
  const nm = ns === 0 ? (m + 1) % 60 : m;
  const nh = nm === 0 && ns === 0 ? h + 1 : h;
  return [nh, nm, ns].map(n => String(n).padStart(2, '0')).join(':');
}

export interface AppShellProps { C: ColorPalette; children: ReactNode; }

export function AppShell({ C, children }: AppShellProps) {
  const location = useLocation();
  const { isOperationModalOpen, isCloseModalOpen, selectedOperation } = useApp();
  const activeId = location.pathname.replace('/', '') || 'map';
  const meta = SCREEN_META[activeId] ?? { title: 'Vigilo', sub: '' };

  const [simTime, setSimTime] = useState(() => {
    const now = new Date();
    return [now.getHours(), now.getMinutes(), now.getSeconds()]
      .map(n => String(n).padStart(2, '0')).join(':');
  });

  useEffect(() => {
    const id = setInterval(() => setSimTime(prev => tickClock(prev)), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{
      display: 'flex', height: '100vh', overflow: 'hidden',
      background: C.bg, color: C.text, fontFamily: "'Albert Sans', sans-serif",
    }}>
      <Sidebar C={C} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Topbar C={C} title={meta.title} sub={meta.sub} simTime={simTime} />
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>
      {isOperationModalOpen && <OperationCreateModal />}
      {isCloseModalOpen && selectedOperation && <CloseOperationModal />}
    </div>
  );
}
